import { useCallback } from 'react';
import { useCheckDuplicatedTagsApi } from './api/tag.hook';
import {
  CheckDuplicatedRequest,
  DuplicatedTag,
  TagsByLabel,
  DuplicatedTagsByForm,
  TaggedForm,
} from '../services/http/tag.api';
import TagFormat from '../utils/tagFormat';

interface UseCheckDuplicatedTagsProps {
  onDuplicatesFound?: (duplicatedTags: DuplicatedTagsByForm) => void;
  ignoreDocumentId?: number;
}

interface UseCheckDuplicatedTagsReturn {
  checkDuplicates: (forms: any[]) => Promise<{
    hasDuplicates: boolean;
    originalDuplicatedValues: DuplicatedTag[];
    duplicatedTagsByForm: DuplicatedTagsByForm;
  }>;
  checkDuplicatesForSingleForm: (form: any) => Promise<{
    hasDuplicates: boolean;
    originalDuplicatedValues: DuplicatedTag[];
  }>;
}

const convertToTaggedForm = (form: any): TaggedForm => ({
  internalId: form.internalId,
  tagList: form.tagList.map((tag: any) => ({
    tagLabel: tag.tagLabel,
    tagValues: Array.isArray(tag.tagValues)
      ? tag.tagValues
      : [TagFormat.normalizeTagValue(tag.format, tag.value)].filter(Boolean),
  })),
});

const extractTagsByLabel = (forms: TaggedForm[]): TagsByLabel => {
  const tagsByLabel: TagsByLabel = {};

  forms.forEach((form) => {
    form.tagList.forEach((tag) => {
      if (tag.tagValues && tag.tagValues.length > 0) {
        if (!tagsByLabel[tag.tagLabel]) {
          tagsByLabel[tag.tagLabel] = [];
        }
        tagsByLabel[tag.tagLabel].push(...tag.tagValues);
      }
    });
  });

  return tagsByLabel;
};

const buildCheckDuplicatedRequest = (
  tagsByLabel: TagsByLabel,
  ignoreDocumentId?: number,
): CheckDuplicatedRequest => ({
  tagList: Object.entries(tagsByLabel).map(([tagLabel, tagValues]) => ({
    tagLabel,
    tagValues,
  })),
  ignoreDocumentId,
});

const findFormDuplicates = (
  form: TaggedForm,
  duplicatedTags: DuplicatedTag[],
): DuplicatedTag[] => {
  const formDuplicates: DuplicatedTag[] = [];

  duplicatedTags.forEach((duplicatedTag) => {
    const hasMatchingTag = form.tagList.some(
      (tag) => tag.tagLabel === duplicatedTag.tagLabel
        && tag.tagValues.some((value) => duplicatedTag.tagValues.includes(value)),
    );

    if (hasMatchingTag) {
      formDuplicates.push(duplicatedTag);
    }
  });

  return formDuplicates;
};

const mapDuplicatesToForms = (
  forms: TaggedForm[],
  duplicatedTags: DuplicatedTag[],
): DuplicatedTagsByForm => {
  const duplicatedTagsByForm: DuplicatedTagsByForm = {};

  forms.forEach((form) => {
    const formDuplicates = findFormDuplicates(form, duplicatedTags);

    if (formDuplicates.length > 0) {
      const formId = form.internalId || 'form-temp';
      duplicatedTagsByForm[formId] = formDuplicates;
    }
  });

  return duplicatedTagsByForm;
};

export default function useCheckDuplicatedTags({
  onDuplicatesFound,
  ignoreDocumentId,
}: UseCheckDuplicatedTagsProps = {}): UseCheckDuplicatedTagsReturn {
  const checkDuplicatedTagsApi = useCheckDuplicatedTagsApi();

  const checkDuplicates = useCallback(async (forms: any[]) => {
    const taggedForms = forms.map(convertToTaggedForm);

    const tagsByLabel = extractTagsByLabel(taggedForms);

    const request = buildCheckDuplicatedRequest(tagsByLabel, ignoreDocumentId);

    const response = await checkDuplicatedTagsApi.request(request);

    if (response.duplicatedTagList && response.duplicatedTagList.length > 0) {
      const duplicatedTagsByForm = mapDuplicatesToForms(taggedForms, response.duplicatedTagList);

      if (onDuplicatesFound) {
        onDuplicatesFound(duplicatedTagsByForm);
      }

      return {
        hasDuplicates: true,
        originalDuplicatedValues: response.duplicatedTagList,
        duplicatedTagsByForm,
      };
    }

    if (onDuplicatesFound) {
      onDuplicatesFound({});
    }

    return {
      hasDuplicates: false,
      originalDuplicatedValues: [],
      duplicatedTagsByForm: {},
    };
  }, [checkDuplicatedTagsApi, ignoreDocumentId, onDuplicatesFound]);

  const checkDuplicatesForSingleForm = useCallback(async (form: any) => {
    const result = await checkDuplicates([form]);
    return {
      hasDuplicates: result.hasDuplicates,
      originalDuplicatedValues: result.originalDuplicatedValues,
    };
  }, [checkDuplicates]);

  return {
    checkDuplicates,
    checkDuplicatesForSingleForm,
  };
}
